import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Product from 'data/Product';
import Country from 'data/Country';
import * as Arr from 'lib/Array';
import { Border, Text, Background } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { BorderRadius } from 'styles/themeBorderRadius';
import {
  AFFILIATE_FORM_LABELS,
  AFFILIATE_FORM_LOAD_TIMEOUT_MS,
  AFFILIATE_FORM_REGIONS,
  AffiliateFormRegion,
  COGNITO_FORMS_BASE_URL,
  REGIONS,
  buildCognitoFormUrl
} from 'constants/affiliateForm';
import RegionFormConfig, { PanelDivider } from './RegionFormConfig';
import CheckoutFormPreview from './CheckoutFormPreview';

const Container = styled.div`
  width: 100%;
  padding-bottom: ${Space.sp4x};
`;

const Card = styled.section`
  border: 1px solid ${Border.light};
  border-radius: ${BorderRadius.m};
  background-color: ${Background.white};
  padding: ${Space.sp3x};
  margin-bottom: ${Space.sp3x};
`;

const CardTitle = styled.h3`
  margin: 0 0 ${Space.spHalf} 0;
  font-size: 18px;
  color: ${Text.primary};
`;

const CardSubtitle = styled.p`
  margin: 0 0 ${Space.sp2x} 0;
  font-size: 14px;
  color: ${Text.secondary};
`;

const RegionPanels = styled.div`
  display: flex;
  border: 1px solid ${Border.light};
  border-radius: ${BorderRadius.m};
`;

/**
 * Per-region UI state. Each field carries its own meaning rather than being mirrored by a
 * separate status enum: a non-null `pendingFormId` *is* "applying", a non-empty
 * `errorMessage` *is* "errored". That removes the need to update two fields in lockstep on
 * every transition.
 */
type RegionUiState = {
  inputValue: string;
  /** Candidate id awaiting the existence check. Not yet committed to the product. */
  pendingFormId: string | null;
  /**
   * The id the existence check confirmed Cognito actually serves a form at — set at the same
   * moment it is committed to the product, and also what the preview is pointed at. The frame
   * therefore only ever renders a checked id.
   */
  verifiedFormId: string | null;
  errorMessage: string;
  isLoaded: boolean;
};

const initialRegionState = (inputValue = ''): RegionUiState => ({
  inputValue,
  pendingFormId: null,
  verifiedFormId: null,
  errorMessage: '',
  isLoaded: false
});

/**
 * Every region's UI state. Owned by the page rather than by this view: switching product
 * tabs unmounts the view, and a typed-but-not-yet-applied form id (and any error against it)
 * has to survive that the same way an edited variant row does. `null` means "not seeded from
 * the product yet".
 */
export type AffiliateFormState = Record<AffiliateFormRegion, RegionUiState>;

// Stands in for an unseeded state so the view can render (and its hooks can run) before the
// countries list resolves. Never mutated — patches always build a new object.
const EMPTY_AFFILIATE_FORM_STATE: AffiliateFormState = {
  US: initialRegionState(),
  CA: initialRegionState()
};

/** True while any region is showing a form-id error, i.e. the product must not be submitted. */
export const hasAffiliateFormError = (state: AffiliateFormState | null): boolean =>
  !!state && AFFILIATE_FORM_REGIONS.some(region => !!state[region].errorMessage);

export interface Props {
  product: Product;
  update: (product: Product) => void;
  countries: Country[];
  disabled?: boolean;
  /** Persisted per-region state, or `null` until it has been seeded from the product. */
  formState: AffiliateFormState | null;
  onFormStateChange: React.Dispatch<React.SetStateAction<AffiliateFormState | null>>;
}

/**
 * Seeds `formState` from the product on first mount and never again — committing an applied
 * id mutates the product, and re-seeding on that would wipe the "Preview loaded" status the
 * commit just set (AC 5). The page clears `formState` when a different product is opened
 * (AC 6), which is what triggers the next seed.
 */
const AffiliateFormView = ({
  product,
  update,
  countries,
  disabled = false,
  formState,
  onFormStateChange
}: Props) => {
  const [selectedRegion, setSelectedRegion] = useState<AffiliateFormRegion>('US');

  // ProductCognitoForm rows are keyed by the real countryId (DB-assigned), not by region —
  // resolve each region's id once the countries list has loaded.
  const regionCountryId = useMemo(() => {
    const map = {} as Record<AffiliateFormRegion, number | undefined>;
    AFFILIATE_FORM_REGIONS.forEach(region => {
      map[region] = countries.find(c => c.countryIso === REGIONS[region].countryIso)?.id;
    });
    return map;
  }, [countries]);

  const formForRegion = useCallback(
    (region: AffiliateFormRegion) => {
      const countryId = regionCountryId[region];
      return countryId != null
        ? product?.cognitoForms?.find(form => form.countryId === countryId)
        : undefined;
    },
    [product, regionCountryId]
  );

  const regionState = formState ?? EMPTY_AFFILIATE_FORM_STATE;

  // Seed the saved form ids into the fields once — but only after `countries` has resolved,
  // since the ProductCognitoForm rows can't be matched to a region without it. `countries`
  // loads asynchronously and independently of `product`, so a deep link straight into this
  // tab can mount before it arrives. The `formState !== null` guard makes this a one-shot:
  // once seeded, neither a later commit nor a tab switch re-runs it.
  useEffect(() => {
    if (formState !== null || countries.length === 0) return;

    onFormStateChange({
      US: initialRegionState(formForRegion('US')?.cognitoFormId ?? ''),
      CA: initialRegionState(formForRegion('CA')?.cognitoFormId ?? '')
    });
  }, [formState, countries.length, formForRegion, onFormStateChange]);

  const patchRegion = useCallback(
    (region: AffiliateFormRegion, patch: Partial<RegionUiState>) => {
      onFormStateChange(prev => {
        const base = prev ?? EMPTY_AFFILIATE_FORM_STATE;
        return { ...base, [region]: { ...base[region], ...patch } };
      });
    },
    [onFormStateChange]
  );

  const handleChange = useCallback(
    (region: AffiliateFormRegion, value: string) => {
      // Typing clears a stale result so it isn't attributed to a newly entered id.
      patchRegion(region, { inputValue: value, errorMessage: '', isLoaded: false });
    },
    [patchRegion]
  );

  const failRegion = useCallback(
    (region: AffiliateFormRegion, message: string) => {
      // AC 9 — on failure the pending id is dropped and whatever was already configured for
      // this region (and the other region) is left untouched.
      patchRegion(region, {
        pendingFormId: null,
        verifiedFormId: null,
        errorMessage: message,
        isLoaded: false
      });
    },
    [patchRegion]
  );

  const applyTokenRef = useRef<Record<AffiliateFormRegion, number>>({ US: 0, CA: 0 });

  const applyTimerRef = useRef<Record<AffiliateFormRegion, ReturnType<typeof setTimeout> | null>>({
    US: null,
    CA: null
  });

  useEffect(
    () => () => {
      AFFILIATE_FORM_REGIONS.forEach(region => {
        const timer = applyTimerRef.current[region];
        if (timer) clearTimeout(timer);
      });
    },
    []
  );

  const productRef = useRef(product);
  productRef.current = product;

  const commitRegion = useCallback(
    (region: AffiliateFormRegion, formId: string) => {
      const countryId = regionCountryId[region];
      if (countryId == null) return;

      const current = productRef.current;
      const nextForms = Arr.addOrReplace(
        current.cognitoForms ?? [],
        form => form.countryId,
        { countryId, cognitoFormId: formId }
      );
      update({ ...current, cognitoForms: nextForms });
      patchRegion(region, {
        pendingFormId: null,
        verifiedFormId: formId,
        errorMessage: '',
        isLoaded: true
      });
    },
    [regionCountryId, update, patchRegion]
  );

  const handleApply = useCallback(
    (region: AffiliateFormRegion) => {
      const trimmed = regionState[region].inputValue.trim();

      if (!trimmed) {
        patchRegion(region, { inputValue: trimmed });
        failRegion(region, AFFILIATE_FORM_LABELS.ERROR_REQUIRED);
        return;
      }

      if (!COGNITO_FORMS_BASE_URL || regionCountryId[region] == null) {
        failRegion(region, AFFILIATE_FORM_LABELS.ERROR_NOT_CONFIGURED);
        return;
      }

      const token = ++applyTokenRef.current[region];

      // AC 4 — applying always brings its own region into view so the user sees the result.
      setSelectedRegion(region);
      patchRegion(region, {
        inputValue: trimmed,
        pendingFormId: trimmed,
        verifiedFormId: null,
        errorMessage: '',
        isLoaded: false
      });

      const clearTimer = () => {
        const timer = applyTimerRef.current[region];
        if (timer) clearTimeout(timer);
        applyTimerRef.current[region] = null;
      };

      clearTimer();
      applyTimerRef.current[region] = setTimeout(() => {
        if (applyTokenRef.current[region] !== token) return;
        failRegion(region, AFFILIATE_FORM_LABELS.ERROR_UNAVAILABLE);
      }, AFFILIATE_FORM_LOAD_TIMEOUT_MS);

      fetch(buildCognitoFormUrl(trimmed))
        .then(response => {
          if (applyTokenRef.current[region] !== token) return; // superseded by a newer apply
          clearTimer();
          if (!response.ok) {
            failRegion(region, AFFILIATE_FORM_LABELS.ERROR_INVALID_FORM_ID);
            return;
          }
          commitRegion(region, trimmed);
        })
        .catch(() => {
          if (applyTokenRef.current[region] !== token) return;
          clearTimer();
          failRegion(region, AFFILIATE_FORM_LABELS.ERROR_UNAVAILABLE);
        });
    },
    [regionState, regionCountryId, patchRegion, failRegion, commitRegion]
  );

  const selectedState = regionState[selectedRegion];
  const pendingFormId = selectedState.pendingFormId;
  const isLoading = pendingFormId !== null;

  const savedFormId = formForRegion(selectedRegion)?.cognitoFormId;

  const previewUrl = useMemo(() => {
    // A failed apply must not fall back to showing the previously-saved form — the user is
    // looking at an error for the id they just tried, and a preview underneath it would read
    // as "it actually worked". Only once verifiedFormId or a fresh apply clears the error does
    // a preview (candidate or saved) resume.
    if (selectedState.errorMessage) return null;

    // While applying, the iframe only ever loads a candidate once its existence check has
    // passed (verifiedFormId) — until then, fall back to whatever is already saved so an
    // invalid candidate is never given the chance to load.
    const formId = selectedState.verifiedFormId || savedFormId;
    if (!formId || !COGNITO_FORMS_BASE_URL) return null;
    return buildCognitoFormUrl(formId);
  }, [selectedState.errorMessage, selectedState.verifiedFormId, savedFormId]);

  return (
    <Container>
      <Card>
        <CardTitle>{AFFILIATE_FORM_LABELS.CONFIG_TITLE}</CardTitle>
        <CardSubtitle>{AFFILIATE_FORM_LABELS.CONFIG_SUBTITLE}</CardSubtitle>
        <RegionPanels>
          {AFFILIATE_FORM_REGIONS.map((region, index) => (
            <React.Fragment key={region}>
              {index > 0 && <PanelDivider />}
              <RegionFormConfig
                region={region}
                value={regionState[region].inputValue}
                pendingFormId={regionState[region].pendingFormId}
                errorMessage={regionState[region].errorMessage}
                isLoaded={regionState[region].isLoaded}
                disabled={disabled || regionCountryId[region] == null}
                onChange={handleChange}
                onApply={handleApply}
              />
            </React.Fragment>
          ))}
        </RegionPanels>
      </Card>

      <Card>
        <CardTitle>{AFFILIATE_FORM_LABELS.PREVIEW_TITLE}</CardTitle>
        <CheckoutFormPreview
          selectedRegion={selectedRegion}
          onSelectRegion={setSelectedRegion}
          previewUrl={previewUrl}
          isLoading={isLoading}
        />
      </Card>
    </Container>
  );
};

export default AffiliateFormView;
